import React, { useEffect, useState, useRef } from 'react';
import { config } from '../config';
import { mockTrainStopData } from '../mockData';

interface Arrival {
  id: string;
  routeId: string;
  headsign: string;
  destination: string;
  subRoute: string;
  direction: string;
  arrive: number;
  arriveScheduled: number;
  realtime: boolean;
}

interface StopData {
  id: string;
  publicCode: string;
  name: string;
  coordinates: [number, number];
  routes: string[];
  arrivals: Arrival[];
}

interface TrainLine {
  name: string;
  patternId: string;
  color: string;
  arrivals: Arrival[];
}

const TrainView: React.FC = () => {
  const [trainLines, setTrainLines] = useState<TrainLine[]>([
    { name: 'Red Line', patternId: '66:1', color: '#E31837', arrivals: [] },
    { name: 'Green Line', patternId: '68:2', color: '#00A651', arrivals: [] },
    { name: 'Blue Line', patternId: '67:4', color: '#0072CE', arrivals: [] },
    { name: 'Waterfront Line', patternId: '69:1', color: '#8B4513', arrivals: [] }
  ]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const hasFetchedData = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Clock timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatClockTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return '';
    return `Updated ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  // Tower City and other major train stops
  const trainStopIds = ['28916', '29008'];

  useEffect(() => {
    if (hasFetchedData.current && trainLines.some(line => line.arrivals.length > 0)) {
      console.log('Skipping train data fetch - already loaded');
      return;
    }

    const fetchTrainData = async () => {
      try {
        let allStopData: StopData[];

        if (config.useMocks) {
          // Use mock data
          allStopData = trainStopIds.map(stopId => mockTrainStopData(stopId));
        } else {
          // Fetch real data
          // Add 2 weeks (14 days) to current time to access the new feed that starts Dec 7
          const timestamp = config.twoWeekOffset
            ? Date.now() + (14 * 24 * 60 * 60 * 1000)
            : Date.now();
          const promises = trainStopIds.map(train =>
            fetch(
              `${config.services.RTA.stopDetails}${train}?timestamp=${timestamp}`,
              {
                headers: {
                  'X-Organization-Id': 'c3b8f441-7a4b-46a9-b582-2c7ac3cb6f3f'
                }
              }
            ).then(res => res.json())
          );

          allStopData = await Promise.all(promises);
        }
        
        console.log('Fetched train stop data:', allStopData);

        // Collect all arrivals
        const allArrivals: Arrival[] = allStopData.flatMap(stopData => stopData.arrivals);

        console.log('All train arrivals:', allArrivals);

        // Update each train line with its arrivals
        // Match by route number (before the colon) to catch all pattern variants
        const updatedLines = trainLines.map(line => {
          const routeNumber = line.patternId.split(':')[0];
          const lineArrivals = allArrivals
            .filter(arrival => arrival.routeId === routeNumber)
            .sort((a, b) => a.arrive - b.arrive)
            .slice(0, 3);

          console.log(`${line.name} (route ${routeNumber}):`, lineArrivals);

          return { ...line, arrivals: lineArrivals };
        });

        setTrainLines(updatedLines);
        hasFetchedData.current = true;
        setLastUpdated(new Date());
        setLoading(false);
      } catch (error) {
        console.error('Error fetching train data:', error);
        setLoading(false);
      }
    };

    fetchTrainData();
    const interval = setInterval(fetchTrainData, config.dataRefreshInterval);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getMinutesUntil = (timestamp: number): number => {
    const now = Date.now();
    // If using 2-week offset, subtract it from the arrival timestamp to get correct minutes
    const adjustedTimestamp = config.twoWeekOffset
      ? timestamp - (14 * 24 * 60 * 60 * 1000)
      : timestamp;
    return Math.max(0, Math.floor((adjustedTimestamp - now) / 60000));
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading train times...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img
          src="/cavs-logo-2.png"
          alt="Cleveland Cavaliers"
          style={styles.logo}
        />
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.headerTitle}>Upcoming Train Departures</h1>
            <div style={styles.stationSubtitle}>from Tower City Station</div>
          </div>
          {!isMobile && (
            <div style={styles.dateTime}>
              <span style={styles.time}>{formatClockTime(currentTime)}</span>
              <span style={styles.date}>{formatDate(currentTime)}</span>
            </div>
          )}
        </div>
      </div>

      <div style={styles.content}>
        <div style={isMobile ? styles.tablesSectionMobile : styles.tablesSection}>
          {/* Top row - 3 cards */}
          <div style={isMobile ? styles.topRowMobile : styles.topRow}>
            {trainLines.slice(0, 3).map(line => (
              <div key={line.patternId} style={styles.lineCard}>
                <div style={{
                  ...styles.lineName,
                  color: line.color,
                  borderBottomColor: line.color
                }}>
                  {line.name}
                </div>

                <div style={isMobile ? styles.lineBodyMobile : styles.lineBody}>
                  {line.arrivals.length === 0 ? (
                    <div style={isMobile ? styles.noArrivalsMobile : styles.noArrivals}>No upcoming trains</div>
                  ) : (
                    <div style={styles.arrivalsList}>
                      {line.arrivals.map((arrival, index) => {
                        const minutesUntil = getMinutesUntil(arrival.arrive);
                        return (
                          <div key={`${arrival.id}-${index}`} style={styles.arrivalRow}>
                            <div style={styles.arrivalInfo}>
                              <div style={styles.destination}>{arrival.destination}</div>
                              <div style={styles.arrivalTime}>{formatTime(arrival.arrive)}</div>
                            </div>
                            <div style={styles.minValue}>
                              <span style={minutesUntil <= 5 ? styles.urgentMin : styles.normalMin}>
                                {minutesUntil <= 0 ? 'NOW' : `${minutesUntil}`}
                              </span>
                              {minutesUntil > 0 && <span style={styles.minUnit}>min</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom row - 1 card + map */}
          <div style={isMobile ? styles.bottomRowMobile : styles.bottomRow}>
            {trainLines.slice(3, 4).map(line => (
              <div key={line.patternId} style={styles.lineCard}>
                <div style={{
                  ...styles.lineName,
                  color: line.color,
                  borderBottomColor: line.color
                }}>
                  {line.name}
                </div>

                <div style={isMobile ? styles.lineBodyMobile : styles.lineBody}>
                  {line.arrivals.length === 0 ? (
                    <div style={isMobile ? styles.noArrivalsMobile : styles.noArrivals}>No upcoming trains</div>
                  ) : (
                    <div style={styles.arrivalsList}>
                      {line.arrivals.map((arrival, index) => {
                        const minutesUntil = getMinutesUntil(arrival.arrive);
                        return (
                          <div key={`${arrival.id}-${index}`} style={styles.arrivalRow}>
                            <div style={styles.arrivalInfo}>
                              <div style={styles.destination}>{arrival.destination}</div>
                              <div style={styles.arrivalTime}>{formatTime(arrival.arrive)}</div>
                            </div>
                            <div style={styles.minValue}>
                              <span style={minutesUntil <= 5 ? styles.urgentMin : styles.normalMin}>
                                {minutesUntil <= 0 ? 'NOW' : `${minutesUntil}`}
                              </span>
                              {minutesUntil > 0 && <span style={styles.minUnit}>min</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div style={isMobile ? styles.mapContainerMobile : styles.mapContainer}>
              <img
                src="/rta-map.png"
                alt="RTA Rail Map"
                style={styles.mapImage}
              />
            </div>
          </div>

          <div style={isMobile ? styles.footerMobile : styles.footer}>
            <div style={styles.realtimeBadge}>
              <span style={styles.liveDot}></span>
              LIVE UPDATES
            </div>
            {lastUpdated && <div style={styles.footerUpdated}>{formatLastUpdated(lastUpdated)}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#000000',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    backgroundColor: '#860038',
    padding: '1vh 2vw',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    gap: '1vw',
    borderBottom: '2px solid #fdbb30',
    minHeight: '8vh',
    flexShrink: 0
  },
  logo: {
    height: '7vh',
    maxHeight: '80px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))'
  },
  headerContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minWidth: 0
  },
  headerTitle: {
    color: '#fdbb30',
    fontSize: 'clamp(18px, 2vw, 28px)',
    fontWeight: '900',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
  },
  dateTime: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0
  },
  time: {
    color: '#fdbb30',
    fontSize: 'clamp(16px, 1.8vw, 26px)',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  date: {
    color: 'rgba(253, 187, 48, 0.8)',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  },
  stationSubtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    fontWeight: '600',
    letterSpacing: '1px'
  },

  content: {
    display: 'flex',
    flex: 1
  },
  tablesSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 'clamp(15px, 2vh, 30px)',
    overflow: 'auto',
    background: 'linear-gradient(135deg, #000000 0%, #860038 100%)',
    gap: 'clamp(10px, 1.5vh, 20px)',
    minHeight: 0
  },
  tablesSectionMobile: {
    display: 'flex',
    flexDirection: 'column',
    padding: '15px',
    background: 'linear-gradient(135deg, #000000 0%, #860038 100%)',
    gap: '15px'
  },
  topRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'clamp(10px, 1.5vh, 20px)',
    flex: 1,
    minHeight: 0
  },
  topRowMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  bottomRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: 'clamp(10px, 1.5vh, 20px)',
    flex: 1,
    minHeight: 0
  },
  bottomRowMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  mapContainer: {
    backgroundColor: 'white',
    borderRadius: 'clamp(8px, 1vh, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(10px, 1.5vh, 20px)',
    border: 'clamp(2px, 0.3vh, 3px) solid #fdbb30',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    minHeight: 0,
    overflow: 'hidden'
  },
  mapContainerMobile: {
    backgroundColor: 'white',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '15px',
    border: '2px solid #fdbb30',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    height: '300px'
  },
  mapImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  lineCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 'clamp(6px, 0.8vh, 8px)',
    border: 'clamp(1px, 0.2vh, 2px) solid #fdbb30',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  },
  lineName: {
    fontSize: 'clamp(14px, 1.5vw, 20px)',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    backgroundColor: '#000',
    padding: 'clamp(8px, 1vh, 12px) clamp(12px, 1.2vw, 16px)',
    borderBottom: 'clamp(1px, 0.2vh, 2px) solid',
    flexShrink: 0
  },
  lineBody: {
    flex: 1,
    padding: 'clamp(8px, 1vh, 12px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(6px, 0.8vh, 8px)',
    overflow: 'auto',
    minHeight: 0
  },
  lineBodyMobile: {
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  noArrivals: {
    padding: 'clamp(15px, 2vh, 20px)',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    fontStyle: 'italic'
  },
  noArrivalsMobile: {
    padding: '20px',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '14px',
    fontStyle: 'italic'
  },
  arrivalsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(6px, 0.8vh, 8px)'
  },
  arrivalRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 'clamp(4px, 0.6vh, 6px)',
    padding: 'clamp(8px, 1vh, 12px) clamp(12px, 1.2vw, 16px)',
    gap: 'clamp(8px, 1vw, 12px)'
  },
  arrivalInfo: {
    flex: 1,
    minWidth: 0
  },
  destination: {
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    fontWeight: '700',
    color: '#000',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  arrivalTime: {
    fontSize: 'clamp(11px, 1vw, 14px)',
    fontWeight: '600',
    color: '#666',
    whiteSpace: 'nowrap'
  },
  minValue: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 'clamp(2px, 0.3vw, 3px)',
    flexShrink: 0
  },
  urgentMin: {
    fontSize: 'clamp(18px, 2vw, 26px)',
    fontWeight: '900',
    color: '#860038'
  },
  normalMin: {
    fontSize: 'clamp(16px, 1.8vw, 24px)',
    fontWeight: '700',
    color: '#860038'
  },
  minUnit: {
    fontSize: 'clamp(10px, 1vw, 13px)',
    fontWeight: '600',
    color: '#666'
  },
  loadingText: {
    color: '#fdbb30',
    fontSize: 'clamp(20px, 2.5vw, 32px)',
    fontWeight: '700',
    textAlign: 'center',
    margin: 'auto'
  },
  footer: {
    textAlign: 'center',
    paddingTop: 'clamp(10px, 1.5vh, 20px)',
    flexShrink: 0
  },
  footerMobile: {
    textAlign: 'center',
    paddingTop: '20px',
    marginTop: '10px',
    flexShrink: 0
  },
  footerUpdated: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 'clamp(11px, 1vw, 14px)',
    fontWeight: '500',
    marginTop: 'clamp(4px, 0.5vh, 6px)'
  },
  realtimeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'clamp(8px, 1vw, 12px)',
    backgroundColor: '#860038',
    color: '#fdbb30',
    padding: 'clamp(8px, 1vh, 12px) clamp(16px, 1.8vw, 24px)',
    borderRadius: 'clamp(15px, 1.5vh, 20px)',
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    fontWeight: '900',
    letterSpacing: '1px',
    boxShadow: '0 4px 12px rgba(134, 0, 56, 0.4)',
    border: '1px solid #fdbb30'
  },
  liveDot: {
    width: 'clamp(8px, 1vw, 12px)',
    height: 'clamp(8px, 1vw, 12px)',
    backgroundColor: '#fdbb30',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'pulse 2s ease-in-out infinite',
    boxShadow: '0 0 8px #fdbb30'
  }
};

export default TrainView;
